import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Wind, Layers, Cpu, Zap, Shield, Activity, ArrowRight } from 'lucide-react';
import { technologyFeatures, turbineModels } from '../mock';

const Technology = () => {
  const navigate = useNavigate();
  
  const iconMap = {
    Wind: Wind,
    Layers: Layers,
    Cpu: Cpu,
    Zap: Zap,
    Shield: Shield,
    Activity: Activity
  };

  return (
    <div className="technology-page">
      {/* Header */}
      <section className="page-header">
        <div className="container">
          <h1 className="page-title">Revolutionary Wind Turbine Technology</h1>
          <p className="page-subtitle">
            Autonomous Cloud-Optimised Single-Blade Vertical Axis Wind Turbine System
          </p>
        </div>
      </section>

      {/* Main Technology Showcase */}
      <section className="tech-showcase">
        <div className="container">
          <div className="tech-grid">
            <div className="tech-visual">
              <img 
                src="https://images.unsplash.com/photo-1673236394928-fdd8ec9d3619" 
                alt="Wind Turbine Design"
                className="tech-image"
              />
              <div className="tech-badge">
                <span className="badge-text">40% Efficiency</span>
              </div>
            </div>
            
            <div className="tech-content">
              <h2 className="tech-title">Single-Blade VAWT Design</h2>
              <p className="tech-description">
                Our revolutionary single-blade vertical axis wind turbine (VAWT) maximises 
                thickness-to-lift ratio while eliminating inter-blade wake turbulence. 
                Optimised tip-speed ratio (TSR) between 3-4 delivers unprecedented efficiency 
                in the small wind class.
              </p>
              
              <div className="tech-specs">
                <div className="spec-item">
                  <div className="spec-label">Swept Area</div>
                  <div className="spec-value">27.5m² (FireFly)</div>
                </div>
                <div className="spec-item">
                  <div className="spec-label">Power Output</div>
                  <div className="spec-value">4kW @ 8.5m/s</div>
                </div>
                <div className="spec-item">
                  <div className="spec-label">Efficiency</div>
                  <div className="spec-value">~40% Betz limit</div>
                </div>
                <div className="spec-item">
                  <div className="spec-label">Lifetime</div>
                  <div className="spec-value">100 years</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Features Grid */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Advanced Technology Stack</h2>
          <p className="section-subtitle">
            Six breakthrough innovations combined into one integrated platform
          </p>
          
          <div className="features-grid">
            {technologyFeatures.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div key={index} className="feature-card">
                  <div className="feature-icon">
                    <IconComponent size={32} />
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Structural Design */}
      <section className="structural-section">
        <div className="container">
          <div className="structural-grid">
            <div className="structural-content">
              <h2 className="section-title">Structural Excellence</h2>
              <h3 className="structural-subtitle">Carbon Fibre Composite Construction</h3>
              
              <div className="structural-features">
                <div className="structural-item">
                  <div className="structural-icon">
                    <Layers size={24} />
                  </div>
                  <div>
                    <h4>Advanced Materials</h4>
                    <p>Carbon fibre composite blade with internal strain gauges for real-time structural monitoring</p>
                  </div>
                </div>
                
                <div className="structural-item">
                  <div className="structural-icon">
                    <Shield size={24} />
                  </div>
                  <div>
                    <h4>Robust Tower Design</h4>
                    <p>Open steel lattice tower with guyed anchoring provides exceptional stability and durability</p>
                  </div>
                </div>
                
                <div className="structural-item">
                  <div className="structural-icon">
                    <Activity size={24} />
                  </div>
                  <div>
                    <h4>Counterweight System</h4>
                    <p>Horizontal strut counterweight ensures perfect balance and minimal mechanical stress</p>
                  </div>
                </div>
              </div>
              
              <div className="lifetime-badge">
                <div className="lifetime-number">100</div>
                <div className="lifetime-text">Year Structure Lifetime</div>
              </div>
            </div>
            
            <div className="structural-visual">
              <img 
                src="https://images.pexels.com/photos/596815/pexels-photo-596815.jpeg" 
                alt="Carbon Fibre Material"
                className="structural-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Control System */}
      <section className="control-section">
        <div className="container">
          <h2 className="section-title">Intelligent Control System</h2>
          <p className="section-subtitle">
            Cloud-optimised algorithms for maximum energy capture
          </p>
          
          <div className="control-grid">
            <div className="control-card">
              <h3>Microcontroller-Based TSR Regulation</h3>
              <ul>
                <li>Real-time wind speed sensing</li>
                <li>Generator AC cycle monitoring</li>
                <li>12-point-per-rotation sampling</li>
                <li>Dynamic load adjustment</li>
              </ul>
            </div>
            
            <div className="control-card">
              <h3>Cloud Communication</h3>
              <ul>
                <li>Data upload every 300 seconds</li>
                <li>Performance analysis & optimization</li>
                <li>Structural integrity checks</li>
                <li>Predictive maintenance alerts</li>
              </ul>
            </div>
            
            <div className="control-card">
              <h3>Security & Management</h3>
              <ul>
                <li>Encrypted firmware</li>
                <li>Hardware-locked optimization</li>
                <li>Remote shutdown capability</li>
                <li>Fleet-wide monitoring</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Turbine Models */}
      <section className="models-section">
        <div className="container">
          <h2 className="section-title">Turbine Models</h2>
          <div className="models-grid">
            {turbineModels.map((model) => (
              <div key={model.id} className="model-card">
                <div className="model-header">
                  <h3 className="model-name">{model.name}</h3>
                  <div className="model-power">{model.power}kW</div>
                </div>
                
                <p className="model-description">{model.description}</p>
                
                <div className="model-specs">
                  <div className="model-spec">
                    <span className="spec-label">Blade Length</span>
                    <span className="spec-value">{model.bladeLength}m</span>
                  </div>
                  <div className="model-spec">
                    <span className="spec-label">Sweep Area</span>
                    <span className="spec-value">{model.sweepArea}m²</span>
                  </div>
                  <div className="model-spec">
                    <span className="spec-label">Annual Output</span>
                    <span className="spec-value">{model.annualOutput} MWh</span>
                  </div>
                  <div className="model-spec">
                    <span className="spec-label">Efficiency</span>
                    <span className="spec-value">{model.efficiency}%</span>
                  </div>
                </div>
                
                <div className="model-features">
                  {model.features.map((feature, idx) => (
                    <div key={idx} className="model-feature">
                      <span className="feature-check">✓</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="model-pricing">
                  <div className="price-label">Installed Cost</div>
                  <div className="price-value">€{model.installedCost.toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="models-cta">
            <Button 
              size="lg"
              className="cta-primary"
              onClick={() => navigate('/calculator')}
            >
              Calculate Your ROI <ArrowRight className="ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Technology;
